import type { MonsterDef } from '../../sim/registry';
import { monsterStats } from '../balance';
import { circle, cone, rect, ring } from '../classes/kit';
import { HOLLOW_KNIGHT } from './keep';
import { mAreas, mBreath, mCombo, mLeap, mLunge, mMelee, mRoar, mShoot, mVolley } from './kit';

/**
 * Rift Delve (horde dungeon, level 30 / ilvl 250+). Swarm demons have low HP
 * and the `swarm` brain (huge aggro, always pressing in) so AoE clearing feels
 * great. `rift_core` is a static breakable crystal; `rift_warden` and
 * `voidmaw` are floor bosses (scripts in src/content/bosses/story.ts).
 */

const VOID = 0xb050ff;
const FEL = 0xff6a2a;

export const RIFT_MONSTERS: MonsterDef[] = [
  {
    id: 'rift_imp', name: 'Rift Imp', grade: 'minion', model: 'mob_imp', scale: 1, radius: 0.4, creature: 'Demon',
    stats: (l) => monsterStats(l, 'minion', { hp: 0.8, speed: 1.15 }),
    skills: [
      mMelee('imp_claw', 'Hellclaw', { shape: cone(2, 70), coef: 1, windup: 0.35, cd: 1.6 }),
      mShoot('imp_spit', 'Cinder Spit', { coef: 0.9, speed: 15, range: 11, windup: 0.5, cd: 4, status: [{ id: 'burn', duration: 3, value: 0.1 }], hue: FEL }),
    ],
    brain: 'swarm', aggroRange: 30, leash: 80, xp: 8, loot: 'demon', weakness: 'holy',
  },
  {
    id: 'rift_hound', name: 'Rift Hound', grade: 'minion', model: 'mob_hound', scale: 1, radius: 0.6, creature: 'Demon',
    stats: (l) => monsterStats(l, 'minion', { hp: 1.3, speed: 1.3 }),
    skills: [
      mMelee('rhound_bite', 'Rift Bite', { shape: cone(2.2, 70), coef: 1.1, windup: 0.35, cd: 1.7 }),
      mLunge('rhound_pounce', 'Void Pounce', { distance: 5, coef: 1.4, windup: 0.45, cd: 6, range: [2.5, 6] }),
    ],
    brain: 'swarm', aggroRange: 30, leash: 80, xp: 10, loot: 'demon', weakness: 'holy',
  },
  {
    id: 'rift_caster', name: 'Rift Caster', grade: 'normal', model: 'mob_rift_caster', scale: 1, radius: 0.45, creature: 'Demon',
    stats: (l) => monsterStats(l, 'normal', { hp: 0.5, speed: 0.95 }),
    skills: [
      mShoot('rcaster_bolt', 'Void Bolt', { coef: 1.2, speed: 14, range: 13, windup: 0.6, cd: 2.6, anim: 'Cast', hue: VOID }),
      mAreas('rcaster_rift', 'Unstable Rift', {
        steps: [{ shape: circle(2.6), anchor: 'point', delay: 1.1, coef: 1.6, element: 'dark' }],
        cd: 8, range: [0, 12], maxRange: 13, anim: 'Cast', hue: VOID,
      }),
    ],
    brain: 'swarm', aggroRange: 30, leash: 80, xp: 14, loot: 'demon', weakness: 'holy',
  },
  {
    // Breakable crystal: no brain, no skills; `immovable` keeps knockbacks from sliding it.
    id: 'rift_core', name: 'Rift Core', grade: 'elite', model: 'rift_core', scale: 1, radius: 1.2, creature: 'Crystal',
    stats: (l) => monsterStats(l, 'elite', { hp: 2.5, speed: 0 }),
    skills: [],
    brain: 'static', aggroRange: 0, leash: 99, xp: 40, loot: 'demon', params: { immovable: true },
  },
  {
    id: 'rift_warden', name: 'Rift Warden', grade: 'named', model: 'mob_golem', scale: 1.6, radius: 1.5, tint: 0x9a5ae0, creature: 'Construct',
    title: 'Rift Guardian', stagger: 450, hpBars: 12, weakness: 'holy',
    stats: (l) => monsterStats(l, 'named', { hp: 2.4, speed: 0.9 }),
    skills: [
      mAreas('rwarden_slam', 'Riftstone Slam', { steps: [{ shape: circle(3.4, 2.4), delay: 0.8, coef: 2.6 }], cd: 3.5, range: [0, 4.5], superArmor: 'push', hue: VOID }),
      mAreas('rwarden_spikes', 'Void Spikes', {
        steps: [-25, 0, 25].map((rot, i) => ({ shape: rect(12, 2.2, 0, rot), delay: 0.9 + i * 0.2, coef: 2.4, reaction: { kind: 'launch' as const, power: 0.5, duration: 0.9 }, element: 'dark' as const })),
        cd: 8, range: [0, 12], superArmor: 'push', hue: VOID,
      }),
      mVolley('rwarden_barrage', 'Crystal Barrage', { coef: 1.4, waves: 2, interval: 0.4, count: 6, spread: 90, speed: 15, reach: 18, windup: 0.8, cd: 9, range: [0, 16], superArmor: 'push', anim: 'Attack2', element: 'dark', hue: VOID }),
      // Mechanic (50%): in-out collapse.
      mAreas('rwarden_collapse', 'Rift Collapse', {
        announce: 'The Rift Warden destabilises the rift!',
        steps: [
          { shape: ring(3.5, 12), delay: 1.2, coef: 3, element: 'dark' },
          { shape: circle(3.5), delay: 2.2, coef: 3, element: 'dark' },
        ],
        cd: 40, range: [0, 99], superArmor: 'full', anim: 'Roar', hue: VOID,
      }),
    ],
    brain: 'boss', aggroRange: 16, leash: 60, xp: 500, loot: 'elite',
  },
  {
    id: 'voidmaw', name: 'Voidmaw', grade: 'named', model: 'mob_hound', scale: 2.6, radius: 1.6, tint: 0x7a3ad0, creature: 'Demon',
    title: 'Rift Guardian', stagger: 520, hpBars: 14, weakness: 'holy',
    stats: (l) => monsterStats(l, 'named', { hp: 2.8, speed: 1.2 }),
    skills: [
      mCombo('voidmaw_rend', 'Rending Maw', {
        hits: [
          { at: 0.4, shape: cone(4, 100), coef: 1 },
          { at: 0.65, shape: cone(4, 100), coef: 1 },
          { at: 0.95, shape: cone(4.4, 110), coef: 1.6, step: 1, reaction: { kind: 'knockback', power: 2.5 } },
        ],
        cd: 4, range: [0, 4.5], superArmor: 'push', hue: VOID,
      }),
      mLeap('voidmaw_pounce', 'Void Pounce', { radius: 3.6, coef: 2.6, maxDistance: 15, land: 0.85, cd: 7, range: [4, 15], superArmor: 'push', hue: VOID }),
      mBreath('voidmaw_breath', 'Abyssal Breath', { radius: 9, angle: 50, coef: 3, count: 5, interval: 0.2, windup: 0.9, cd: 10, range: [0, 8], superArmor: 'push', element: 'dark', hue: VOID }),
      // Pull field (purple = special), then a blast in the middle.
      mAreas('voidmaw_devour', 'Devouring Rift', {
        steps: [
          { shape: circle(9), delay: 0.6, coef: 0.2, duration: 1.6, tick: 0.4, pull: 5, telegraph: 'purple', reaction: { kind: 'none' }, element: 'dark' },
          { shape: circle(3.5), delay: 2.4, coef: 3.5, element: 'dark' },
        ],
        cd: 16, range: [0, 9], superArmor: 'full', anim: 'Breath', hue: VOID,
      }),
      // Summon: the boss script calls rift hounds when the howl lands (0.9 s).
      mRoar('voidmaw_howl', 'Hungering Howl', {
        announce: 'Voidmaw howls, and the rift answers!', duration: 1.6, cd: 30, hue: VOID,
        nova: { shape: circle(6), delay: 0.9, coef: 0.8, telegraph: 'yellow', reaction: { kind: 'knockback', power: 3 } },
      }),
    ],
    brain: 'boss', aggroRange: 16, leash: 60, xp: 500, loot: 'elite',
  },
];

/** Rift Delve roster for the rift director (ids). The elite is shared with Frostfang Keep. */
export const RIFT_ROSTER = {
  swarm: ['rift_imp', 'rift_hound', 'rift_caster'],
  elites: [HOLLOW_KNIGHT.id],
  core: 'rift_core',
  bosses: ['rift_warden', 'voidmaw'],
};
